import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

export interface GameThemeArtifactReferenceDto {
  id: number;
  gameThemeId: number;
  mediaRole: string;
  sequenceOrder: number;
  artifactAssetId: number;
  isEnabled: boolean;
  isDeleted: boolean;
  dateCreated: string;
  dateModified?: string;
}

export type DisplayScopeKey = 'team1' | 'team2' | 'projector' | 'roundSummary' | 'endOfGameSummary';

export interface DisplayPropertiesDto {
  contractVersion: 1;
  team1: DisplayScopePropertiesDto;
  team2: DisplayScopePropertiesDto;
  projector: DisplayScopePropertiesDto;
  roundSummary: DisplayScopePropertiesDto;
  endOfGameSummary: DisplayScopePropertiesDto;
}

export interface DisplayScopePropertiesDto {
  sections: Record<string, { objects: Record<string, DisplayObjectPropertiesDto> }>;
}

export interface DisplayObjectPropertiesDto {
  values: DisplayPropertyValuesDto;
  selected?: DisplayPropertyValuesDto | null;
  unselected?: DisplayPropertyValuesDto | null;
}

export interface DisplayPropertyValuesDto {
  backgroundColor?: string | null;
  imageOpacityPercent?: number | null;
  border?: { color?: string | null; cornerStyle?: 'Rounded' | 'Square' | 'Pill' | null; thicknessPx?: number | null } | null;
  font?: { color?: string | null; sizeRem?: number | null } | null;
  svg?: { source?: string | null; sizePx?: number | null; direction?: 'Horizontal' | 'Vertical' | null; rotationDegrees?: number | null; color?: string | null } | null;
}

export function createEmptyDisplayProperties(): DisplayPropertiesDto {
  const emptyScope = (): DisplayScopePropertiesDto => ({ sections: {} });
  return {
    contractVersion: 1,
    team1: emptyScope(),
    team2: emptyScope(),
    projector: emptyScope(),
    roundSummary: emptyScope(),
    endOfGameSummary: emptyScope()
  };
}

export interface GameThemeDto {
  id: number;
  themeKey: string;
  categoryId: number;
  category: string;
  name: string;
  description?: string;
  isSystem: boolean;
  isEnabled: boolean;
  isDeleted: boolean;
  createdByUserId?: number;
  updatedByUserId?: number;
  dateCreated: string;
  dateModified?: string;
  artifactReferences: GameThemeArtifactReferenceDto[];
  themeVariables: Record<string, string>;
  displayProperties: DisplayPropertiesDto;
}

export interface GameThemeArtifactReferenceUpsertDto {
  mediaRole: string;
  sequenceOrder: number;
  artifactAssetId: number;
}

export interface GameThemeCreateDto {
  themeKey: string;
  categoryId: number;
  name: string;
  description?: string;
  isSystem: boolean;
  isEnabled: boolean;
  artifactReferences: GameThemeArtifactReferenceUpsertDto[];
  themeVariables: Record<string, string>;
  displayProperties: DisplayPropertiesDto;
}

export interface EventThemeAssignmentDto {
  id: number;
  organizationId: number;
  eventId: number;
  gameThemeId: number;
  assignedByUserId?: number;
  dateAssigned: string;
  isEnabled: boolean;
  isDeleted: boolean;
}

export interface EventThemeAssignmentCreateDto {
  organizationId: number;
  eventId: number;
  gameThemeId: number;
  assignedByUserId?: number;
}

export interface GameThemeUpdateDto {
  categoryId: number;
  name: string;
  description?: string;
  isEnabled: boolean;
  artifactReferences: GameThemeArtifactReferenceUpsertDto[];
  themeVariables: Record<string, string>;
  displayProperties: DisplayPropertiesDto;
}

export interface GameThemeCategoryDto {
  id: number;
  name: string;
  createdDate: string;
  updatedDate: string;
}

export interface GameThemeCategoryCreateDto {
  name: string;
}

export interface GameThemeCategoryUpdateDto {
  name: string;
}

@Injectable({ providedIn: 'root' })
export class GameThemeCatalogService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = '/api/gamethemes';

  getAll(): Observable<GameThemeDto[]> {
    return this.http.get<GameThemeDto[]>(this.apiUrl);
  }

  create(payload: GameThemeCreateDto): Observable<GameThemeDto> {
    return this.http.post<GameThemeDto>(this.apiUrl, payload);
  }

  update(id: number, payload: GameThemeUpdateDto): Observable<GameThemeDto> {
    return this.http.put<GameThemeDto>(`${this.apiUrl}/${id}`, payload);
  }

  getCategories(): Observable<GameThemeCategoryDto[]> {
    return this.http.get<GameThemeCategoryDto[]>(`${this.apiUrl}/categories`);
  }

  createCategory(payload: GameThemeCategoryCreateDto): Observable<GameThemeCategoryDto> {
    return this.http.post<GameThemeCategoryDto>(`${this.apiUrl}/categories`, payload);
  }

  updateCategory(id: number, payload: GameThemeCategoryUpdateDto): Observable<GameThemeCategoryDto> {
    return this.http.put<GameThemeCategoryDto>(`${this.apiUrl}/categories/${id}`, payload);
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  getAssignmentByEvent(eventId: number): Observable<EventThemeAssignmentDto | null> {
    return this.http.get<EventThemeAssignmentDto | null>(`${this.apiUrl}/assignments/event/${eventId}`);
  }

  upsertAssignment(payload: EventThemeAssignmentCreateDto): Observable<EventThemeAssignmentDto> {
    return this.http.post<EventThemeAssignmentDto>(`${this.apiUrl}/assignments`, payload);
  }
}